import {
    createTheme 
} from '@mui/material/styles';

export enum TypographyVariants {
    XXL = 'xxl',
    XL = 'xl',
    H1 = 'h1',
    H2 = 'h2',
    H3 = 'h3',
    H4 = 'h4',
    H5 = 'h5',
    H6 = 'h6',
    SMALL = 'small',
    VERY_SMALL = 'verySmall',
    TINY = 'tiny'
}

export enum TypographyWeights {
    LIGHT = 300,
    REGULAR = 400,
    MEDIUM = 500,
    SEMI_BOLD = 600,
    BOLD = 700,
}

const theme = createTheme(({
    typography: {
        fontSize: 16,
        fontFamily: 'Poppins',
        [TypographyVariants.XXL]: {
            fontFamily: 'Poppins',
            fontSize: '48px',
            lineHeight: '56px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.XL]: {
            fontFamily: 'Poppins',
            fontSize: '36px',
            lineHeight: '48px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.H1]: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.H2]: {
            fontFamily: 'Poppins',
            fontSize: '24px',
            lineHeight: '32px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.H3]: {
            fontFamily: 'Poppins',
            fontSize: '20px',
            lineHeight: '28px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.H4]: {
            fontFamily: 'Poppins',
            fontSize: '18px',
            lineHeight: '24px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.H5]: {
            fontFamily: 'Poppins',
            fontSize: '16px',
            lineHeight: '24px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.H6]: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            lineHeight: '20px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.SMALL]: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            lineHeight: '16px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.VERY_SMALL]: {
            fontFamily: 'Poppins',
            fontSize: '10px',
            lineHeight: '15px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
        [TypographyVariants.TINY]: {
            fontFamily: 'Poppins',
            fontSize: '9px',
            lineHeight: '15px',
            color: '#333333',
            fontWeight: 400,
            letterSpacing: 'normal'
        },
    },
    palette: {
        primary: {
            '25': '#F7FCFF',
            '50': '#EFF9FF',
            '75': '#DEF2FF',
            '100': '#CDEBFF',
            '150': '#C0E6FF',
            '200': '#B4E2FF',
            '250': '#A3DBFE',
            '300': '#92D5FE',
            '350': '#87D1FE',
            '400': '#7DCDFE',
            '450': '#7DCDFE',
            '500': '#5DC0FE',
            '600': '#55AFE7',
            '700': '#4288B4',
            '800': '#336A8C',
            '900': '#27516B',
            light: '#EFF9FF',
            main: '#5DC0FE',
            dark: '#4288B4'
        },
        secondary: {
            '50': '#F7EFFE',
            '100': '#E7CDFB',
            '200': '#DBB4F9',
            '300': '#CA92F6',
            '400': '#C07DF4',
            '500': '#B05DF1',
            '600': '#A055DB',
            '700': '#7D42AB',
            '800': '#613385',
            '900': '#4A2765',
            light: '#F7EFFE',
            main: '#B05DF1',
            dark: '#7D42AB'
        },
        success: {
            '50': '#F6FAED',
            '100': '#E3F0C7',
            '200': '#D6E9AC',
            '300': '#C3E086',
            '400': '#B7D96F',
            '500': '#A5D04B',
            '600': '#96BD44',
            '700': '#759435',
            '800': '#5B7229',
            '900': '#455720',
            light: '#F6FAED',
            main: '#A5D04B',
            dark: '#759435'
        },
        warning: {
            '50': '#FEF9EE',
            '100': '#FBEBCC',
            '200': '#F9E1B3',
            '300': '#F6D390',
            '400': '#F5CB7A',
            '500': '#F2BE59',
            '600': '#DCAD51',
            '700': '#AC873F',
            '800': '#856931',
            '900': '#665025',
            light: '#FEF9EE',
            main: '#F2BE59',
            dark: '#AC873F'
        },
        error: {
            '50': '#FEEEEA',
            '100': '#FAC9BF',
            '200': '#F8AFA0',
            '300': '#F58A74',
            '400': '#F37359',
            '500': '#F05030',
            '600': '#DA492C',
            '700': '#AA3922',
            '800': '#842C1A',
            '900': '#652214',
            light: '#FEEEEA',
            main: '#F05030',
            dark: '#AA3922'
        },
        tertiary: {
            '25': '#FFFEF5',
            '50': '#FFFDEB',
            '75': '#FFFAD6',
            '100': '#FFF7C0',
            '150': '#FFF6B1',
            '200': '#FFF4A2',
            '250': '#FFF18D',
            '300': '#FFEE78',
            '350': '#FFED6B',
            '400': '#FFEB5D',
            '450': '#FFE949',
            '500': '#FFE635',
            '600': '#E8D130',
            '700': '#B5A326',
            '800': '#8C7F1D',
            '900': '#6B6116',
            light: '#FFFDEB',
            main: '#FFE635',
            dark: '#B5A326'
        },
        neutral: {
            '25': '#F5F5F5',
            '50': '#EBEBEB',
            '100': '#C0C0C0',
            '200': '#A1A1A1',
            '300': '#767676',
            '400': '#5C5C5C',
            '500': '#333333',
            '600': '#2E2E2E',
            '700': '#242424',
            '800': '#1C1C1C',
            '900': '#151515',
            '1000': '0A0A0A',
            light: '#EBEBEB',
            main: '#333333',
            dark: '#1C1C1C'
        },
        purple: {
            '50': '#F3EEFC',
            '100': '#DACBF4',
            '200': '#C9B1EF',
            '300': '#B08EE8',
            '400': '#A178E3',
            '500': '#8956DC',
            '600': '#7D4EC8',
            '700': '#613D9C',
            '800': '#4B2F79',
            '900': '#3A245C',
            light: '#F3EEFC',
            main: '#8956DC',
            dark: '#613D9C'
        },
        red: {
            '50': '#FEF3F1',
            '100': '#FCDBD4',
            '200': '#FAC9BF',
            '300': '#F8B1A2',
            '400': '#F7A190',
            '500': '#F58A74',
            '600': '#DF7E6A',
            '700': '#AE6252',
            '800': '#874C40',
            '900': '#673A31',
            light: '#FEF3F1',
            main: '#F58A74',
            dark: '#AE6252'
        },
        pink: {
            '50': '#FFF1F6',
            '100': '#FFD2E2',
            '200': '#FFBDD4',
            '300': '#FF9FC1',
            '400': '#FF8CB5',
            '500': '#FF6FA2',
            '600': '#E86593',
            '700': '#B54F73',
            '800': '#8C3D59',
            '900': '#6B2F44',
            light: '#FFF1F6',
            main: '#FF6FA2',
            dark: '#B54F73'
        },
        indigo: {
            '50': '#F2F0FF',
            '100': '#D8D2FF',
            '200': '#C5BCFF',
            '300': '#AB9DFF',
            '400': '#9A8AFF',
            '500': '#816DFF',
            '600': '#7563E8',
            '700': '#5C4DB5',
            '800': '#473C8C',
            '900': '#362E6B',
            light: '#F2F0FF',
            main: '#816DFF',
            dark: '#5C4DB5'
        },
        turquoise: {
            '50': '#EFFBFB',
            '100': '#CEF4F1',
            '200': '#B6EEEB',
            '300': '#95E7E2',
            '400': '#81E2DC',
            '500': '#61DBD3',
            '600': '#58C7C0',
            '700': '#459B96',
            '800': '#357874',
            '900': '#295C59',
            light: '#EFFBFB',
            main: '#61DBD3',
            dark: '#459B96'
        },
        dryJan2025: {
            '50': '#F4E7FF',
            '100': '#E6CFF9',
            '500': '#9640DA',
            '700': '#7124AF',
            light: '#F4E7FF',
            main: '#9640DA',
            dark: '#7124AF'
        },
        soberishMom2025: {
            '50': '#FFF5E7',
            '100': '#FFE8C8',
            '500': '#F19414',
            '700': '#C97A0D',
            light: '#FFF5E7',
            main: '#F19414',
            dark: '#C97A0D'
        },
        soberishMom: {
            '50': '#FFF5E7',
            '100': '#FFE8C8',
            '500': '#F2BE59',
            '700': '#C97A0D',
            light: '#FFF5E7',
            main: '#F2BE59',
            dark: '#C97A0D'
        },
        zumba: {
            '50': '#F8E2FF',
            '100': '#F1C4FF',
            '500': '#B632E0',
            light: '#F8E2FF',
            main: '#B632E0',
            dark: '#B632E0'
        },
        black: '#000000',
        white: '#FFFFFF',
        transparent: '#00000000'
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    [TypographyVariants.XXL]: 'h1',
                    [TypographyVariants.XL]: 'h1',
                    [TypographyVariants.SMALL]: 'p',
                    [TypographyVariants.VERY_SMALL]: 'p',
                    [TypographyVariants.TINY]: 'p'
                }
            }
        }
    },
    shape: {
        borderRadius: 10
    },
}));

export default theme;
